import React, { useState, useEffect } from 'react';
import { 
  Container, Row, Col, Form, Button, Card, Spinner 
} from 'react-bootstrap';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, PieChart, Pie, Cell
} from 'recharts';
import axios from '../api/axios';
import PropTypes from 'prop-types';

const RevenueKPI = ({ outlets = [], loadingOutlets = false }) => {
  const token = localStorage.getItem('authToken');

  const defaultFilters = {
    outlet: "",
    timeRange: 'weekly',
    classType: ""
  };

  const [filters, setFilters] = useState(defaultFilters);

  const [revenueData, setRevenueData] = useState(null);
  const [avgSalesData, setAvgSalesData] = useState([]);
  const [loading, setLoading] = useState(false);

  const pieColors = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

  const fetchRevenueData = async (activeFilters) => {
    try {
      setLoading(true);

      const params = {
        weekly: activeFilters.timeRange === "weekly",
        monthly: activeFilters.timeRange === "monthly",
        ...(activeFilters.classType !== "" && { classType: activeFilters.classType }),
        ...(activeFilters.outlet !== "" && { outletId: activeFilters.outlet }),
      };

      const response = await axios.get('dashboard/getRevenueStatThroughSummary', {
        headers: { Authorization: `Bearer ${token}` },
        params
      });

      const result = response?.data?.data?.data?.[0] || {};
      setRevenueData(result);
      setAvgSalesData(result?.avgSales || []);

    } catch (error) {
      console.error('Error fetching revenue data:', error);
      setRevenueData(null);
      setAvgSalesData([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and whenever filters change
  useEffect(() => {
    fetchRevenueData(filters);
  }, [filters.outlet, filters.timeRange, filters.classType]);

  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({ ...prev, [filterName]: value }));
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  const getRevenueBreakdownData = (data) => {
    if (!data || Object.keys(data).length === 0) return [];
    return [
      { name: 'Total Revenue',   value: data.totalGrandTotal || 0 },
      { name: 'Taxable Amount',  value: data.totalTaxale     || 0 },
      { name: 'Tax Amount',      value: data.totalTaxAmount  || 0 },
      { name: 'Discount',        value: data.totalDiscount   || 0 },
    ];
  };

  const revenueBreakdownData = getRevenueBreakdownData(revenueData);

  const summaryCards = [
    { key: 'totalGrandTotal', title: 'Total Revenue',   icon: 'bi-cash-coin', color: '#007bff' },
    { key: 'totalTaxale',     title: 'Taxable Amount',  icon: 'bi-receipt',   color: '#28a745' },
    { key: 'totalTaxAmount',  title: 'Tax Amount',      icon: 'bi-percent',   color: '#dc3545' },
    { key: 'totalDiscount',   title: 'Discount',        icon: 'bi-tag',       color: '#fd7e14' },
  ];

  return (
    <Container fluid className="py-4 shadow-sm bg-white">
      <h4 className="mb-4 fw-bold">REVENUE ANALYSIS</h4>
      <hr />

      {/* Summary Cards */}
      <Row className="g-4 mb-4">
        {summaryCards.map(({ key, title, icon, color }) => (
          <Col md={3} key={key}>
            <Card className="h-100 shadow-sm border-0">
              <Card.Body className="p-3">
                <div className="d-flex align-items-center mb-2">
                  <i className={`bi ${icon} fs-4 me-2`} style={{ color }} />
                  <Card.Title className="text-muted mb-0" style={{ fontSize: '0.9rem', fontWeight: '500' }}>
                    {title}
                  </Card.Title>
                </div>
                {loading ? (
                  <div className="text-center py-2">
                    <Spinner animation="border" size="sm" style={{ color }} />
                  </div>
                ) : (
                  <Card.Text className="fw-semibold fs-4" style={{ color }}>
                    {typeof revenueData?.[key] === 'number'
                      ? `₹${revenueData[key].toFixed(2)}`
                      : 'N/A'}
                  </Card.Text>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Filter Controls */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Form.Group>
            <Form.Label>Outlet</Form.Label>
            <Form.Select
              value={filters.outlet}
              onChange={(e) => handleFilterChange('outlet', e.target.value)}
              disabled={loadingOutlets}
            >
              <option value="">All Outlets</option>
              {outlets.map(outlet => (
                <option key={outlet._id} value={outlet._id}>{outlet.name}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group>
            <Form.Label>Duration</Form.Label>
            <Form.Select
              value={filters.timeRange}
              onChange={(e) => handleFilterChange('timeRange', e.target.value)}
            >
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={2}>
          <Form.Group>
            <Form.Label>Class Type</Form.Label>
            <Form.Select
              value={filters.classType}
              onChange={(e) => handleFilterChange('classType', e.target.value)}
            >
              <option value="">Both</option>
              <option value="FOOD">Food</option>
              <option value="BEVERAGE">Beverage</option>
            </Form.Select>
          </Form.Group>
        </Col>

        <Col md={2} className="d-flex align-items-end">
          <Button
            variant="outline-secondary"
            onClick={resetFilters}
            disabled={loading}
            className="w-100"
          >
            Reset
          </Button>
        </Col>
      </Row>

      {/* Charts Section */}
      {loading ? (
        <Row className="mb-4">
          <Col className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">Loading revenue data...</p>
          </Col>
        </Row>
      ) : (
        <>
          {/* Revenue Breakdown Pie Chart */}
          <Row className="mb-4">
            <Col md={12}>
              <h4>Revenue Breakdown</h4>
              {revenueBreakdownData.length > 0 ? (
                <div style={{ height: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Tooltip formatter={(value) => [`₹${value.toLocaleString()}`, 'Amount']} />
                      <Legend layout="horizontal" verticalAlign="bottom" align="center" />
                      <Pie
                        data={revenueBreakdownData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius={120}
                        innerRadius={40}
                        paddingAngle={2}
                        label={({ name, value }) => {
                          const total = revenueBreakdownData.reduce((sum, item) => sum + item.value, 0);
                          const percent = total ? (value / total) * 100 : 0;
                          return `${name}: ₹${value.toFixed(2)} (${percent.toFixed(1)}%)`;
                        }}
                        labelLine={false}
                      >
                        {revenueBreakdownData.map((_, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={pieColors[index % pieColors.length]}
                            stroke="#fff"
                            strokeWidth={1}
                          />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="d-flex justify-content-center align-items-center bg-light rounded" style={{ height: '200px' }}>
                  <p className="text-muted">No revenue data available</p>
                </div>
              )}
            </Col>
          </Row>

          {/* Average Sales Bar Chart */}
          {avgSalesData.length > 0 && (
            <Row className="mb-4">
              <Col md={12}>
                <h4>Average Sales</h4>
                <div style={{ height: '400px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={avgSalesData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="_id" angle={-45} textAnchor="end" height={60} />
                      <YAxis />
                      <Tooltip formatter={(value) => [`₹${value.toFixed(2)}`, 'Amount']} />
                      <Legend />
                      <Bar dataKey="avgSale" fill="#82ca9d" name="Average Sale" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
};

RevenueKPI.propTypes = {
  outlets: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      status: PropTypes.string
    })
  ),
  loadingOutlets: PropTypes.bool
};

export default RevenueKPI;